import React, { useCallback, useMemo, useState } from 'react';
import type {
  AiModelConfig,
  AiModelOption,
  AiPromptTemplate,
  AiTestProject,
  AiTestSourceOfTruthOption,
} from './types';
import { createTesterRun } from './api';
import ModelPicker from './ModelPicker';
import PromptPicker from './PromptPicker';
import SourceOfTruthPicker from './SourceOfTruthPicker';

interface JtbdFormProps {
  projects: AiTestProject[];
  models: AiModelOption[];
  prompts: AiPromptTemplate[];
  onPromptsChanged: () => Promise<void> | void;
  onRunStarted: () => Promise<void> | void;
}

const JTBD_STEPS = [
  { key: 'step1', label: 'Step 1: Extract codes' },
  { key: 'step2', label: 'Step 2: Combine buckets' },
  { key: 'step3', label: 'Step 3: Weight buckets' },
  { key: 'step4', label: 'Step 4: Build relationships' },
  { key: 'step5', label: 'Step 5: Transform to JSON' },
  { key: 'step6', label: 'Step 6: Write statements' },
] as const;

type StepKey = (typeof JTBD_STEPS)[number]['key'];

const JtbdForm: React.FC<JtbdFormProps> = ({
  projects,
  models,
  prompts,
  onPromptsChanged,
  onRunStarted,
}) => {
  const [projectId, setProjectId] = useState<number | ''>('');
  const [sourceOfTruth, setSourceOfTruth] = useState<AiTestSourceOfTruthOption | null>(null);
  const [useOpportunitySpace, setUseOpportunitySpace] = useState(true);
  const [useMarkets, setUseMarkets] = useState(true);
  const [appendToProject, setAppendToProject] = useState(true);
  const [modelId, setModelId] = useState('');
  const [modelConfig, setModelConfig] = useState<AiModelConfig>({});
  const [stepPrompts, setStepPrompts] = useState<Partial<Record<StepKey, AiPromptTemplate | null>>>({});
  const [stepContents, setStepContents] = useState<Partial<Record<StepKey, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const project = projects.find((p) => p.id === projectId);
  const promptsByStep = useMemo(() => {
    const grouped: Record<string, AiPromptTemplate[]> = {};
    for (const step of JTBD_STEPS) grouped[step.key] = [];
    for (const prompt of prompts) {
      const key = prompt.step_key || '';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(prompt);
    }
    return grouped;
  }, [prompts]);

  const handleStepContent = useCallback((stepKey: StepKey, content: string) => {
    setStepContents((prev) => (prev[stepKey] === content ? prev : { ...prev, [stepKey]: content }));
  }, []);

  const handleRun = async () => {
    if (!project) {
      setError('Select a project first.');
      return;
    }
    if (!sourceOfTruth) {
      setError('Select a source of truth file.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    const variableKeys = ['source_of_truth'];
    if (useOpportunitySpace) variableKeys.push('opportunity_space');
    if (useMarkets) variableKeys.push('markets');
    const stepPayload: Record<string, Record<string, unknown>> = {};
    for (const step of JTBD_STEPS) {
      const inline = (stepContents[step.key] || '').trim();
      const prompt = stepPrompts[step.key];
      stepPayload[step.key] = {
        prompt_inline: inline || undefined,
        prompt_id: inline ? undefined : prompt?.id || undefined,
        blob_filename: inline || prompt?.id ? undefined : prompt?.blob_filename || undefined,
      };
    }
    try {
      const run = await createTesterRun({
        project_id: project.id,
        generator_key: 'jtbd',
        variable_keys: variableKeys,
        model_id: modelId || undefined,
        model_config: modelConfig,
        source_of_truth: {
          kind: sourceOfTruth.kind,
          job_id: sourceOfTruth.job_id,
          report_url: sourceOfTruth.report_url,
          country: sourceOfTruth.country,
          label: sourceOfTruth.label,
        },
        step_prompts: stepPayload,
        append_to_project: appendToProject,
      });
      setSuccess(`Run #${run.id} started (${run.status}).`);
      await onRunStarted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the run');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-[12px] font-medium text-[#626262] mb-1">Project</label>
        <select
          value={projectId}
          onChange={(e) => {
            setProjectId(e.target.value ? Number(e.target.value) : '');
            setSourceOfTruth(null);
          }}
          className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm"
        >
          <option value="">Select a project</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      <SourceOfTruthPicker
        projectId={projectId}
        value={sourceOfTruth}
        onChange={setSourceOfTruth}
        disabled={submitting}
      />

      {sourceOfTruth && (
        <p className="text-[12px] text-[#626262]">
          Market: <span className="font-medium text-[#0C0B0B]">{sourceOfTruth.country}</span>
        </p>
      )}

      <div>
        <p className="text-[12px] font-medium text-[#626262] mb-2">Prompt variables</p>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-[#8A8A8A]">
            <input type="checkbox" checked disabled />
            Source of Truth
          </label>
          <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
            <input
              type="checkbox"
              checked={useOpportunitySpace}
              onChange={(e) => setUseOpportunitySpace(e.target.checked)}
            />
            Opportunity Space
          </label>
          <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
            <input
              type="checkbox"
              checked={useMarkets}
              onChange={(e) => setUseMarkets(e.target.checked)}
            />
            Market
            {sourceOfTruth && (
              <span className="text-[12px] text-[#8A8A8A]">({sourceOfTruth.country})</span>
            )}
          </label>
        </div>
      </div>

      <ModelPicker
        models={models}
        modelId={modelId}
        onModelId={setModelId}
        config={modelConfig}
        onConfig={setModelConfig}
        generatorKey="jtbd"
        disabled={submitting}
      />

      <div className="space-y-2">
        <p className="text-[12px] font-medium text-[#626262]">Step prompts</p>
        {JTBD_STEPS.map((step, index) => (
          <details key={step.key} open={index === 0} className="rounded-md border border-[#E8E8E8] p-3">
            <summary className="cursor-pointer text-sm font-medium text-[#0C0B0B]">{step.label}</summary>
            <div className="mt-3">
              <StepPromptPicker
                stepKey={step.key}
                label={step.label}
                prompts={promptsByStep[step.key] || []}
                selected={stepPrompts[step.key] || null}
                onSelect={(prompt) => setStepPrompts((prev) => ({ ...prev, [step.key]: prompt }))}
                onContent={handleStepContent}
                onPromptsChanged={onPromptsChanged}
                disabled={submitting}
              />
            </div>
          </details>
        ))}
      </div>

      <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
        <input
          type="checkbox"
          checked={appendToProject}
          onChange={(e) => setAppendToProject(e.target.checked)}
        />
        Append jobs to the project workspace
      </label>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {success && <p className="text-sm text-emerald-700">{success}</p>}

      <button
        type="button"
        onClick={() => void handleRun()}
        disabled={submitting || !project || !sourceOfTruth}
        className="h-9 px-4 rounded-md bg-[#3da3d4] text-white text-sm font-medium disabled:opacity-50"
      >
        {submitting ? 'Starting…' : 'Run Jobs to be Done'}
      </button>
    </div>
  );
};

interface StepPromptPickerProps {
  stepKey: StepKey;
  label: string;
  prompts: AiPromptTemplate[];
  selected: AiPromptTemplate | null;
  onSelect: (prompt: AiPromptTemplate | null) => void;
  onContent: (stepKey: StepKey, content: string) => void;
  onPromptsChanged: () => Promise<void> | void;
  disabled?: boolean;
}

const StepPromptPicker: React.FC<StepPromptPickerProps> = ({
  stepKey,
  label,
  prompts,
  selected,
  onSelect,
  onContent,
  onPromptsChanged,
  disabled,
}) => {
  const onContentChange = useCallback(
    (content: string) => onContent(stepKey, content),
    [onContent, stepKey]
  );
  return (
    <PromptPicker
      label={label}
      generatorKey="jtbd"
      stepKey={stepKey}
      prompts={prompts}
      selectedId={selected?.id ?? null}
      blobFilename={selected?.blob_filename || ''}
      onSelect={onSelect}
      onContentChange={onContentChange}
      onPromptsChanged={onPromptsChanged}
      disabled={disabled}
    />
  );
};

export default JtbdForm;
